import React, { useEffect, useState, useMemo } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { ColumnHeading } from "../../../shared/components/TableElements";
import { useTranslation } from "react-i18next";
import { Card, CardBody } from "../../../shared/components/Card";
import SingleProvider from "./components/SingleProvider";
import { Desc } from "../../Reminders/MaintenanceReminders/MaintenanceRemindersForm";
import { SearchInput } from "../../../shared/components/table/components/TempFilter";
import styled from "styled-components";
import samsaraIcon from "@/shared/img/integrations/logos/samsara-logo.jpg";
import geoTabIcon from "@/shared/img/integrations/logos/geotab_logo.svg";
import streamlineIcon from "@/shared/img/integrations/logos/streamline-logo.png";
import safetyTrackIcon from "@/shared/img/integrations/logos/safety-track-logo.png";
import easyTrackIcon from "@/shared/img/integrations/logos/easy-track-logo.png";
import bouncieIcon from "@/shared/img/integrations/logos/bouncie-logo.jpg";
import motiveIcon from "@/shared/img/integrations/logos/motive-logo.png";
import wialonIcon from "@/shared/img/integrations/logos/wialon-logo.png";
import fleetCompleteIcon from "@/shared/img/integrations/logos/fleet-complete-logo.jpg";
import vestigeViewIcon from "@/shared/img/integrations/logos/vestige-view-logo.jpeg";
import targaIcon from "@/shared/img/integrations/logos/targa-logo.png";
import cartrackIcon from "@/shared/img/integrations/logos/cartrack-logo.svg";
import frotcomIcon from "@/shared/img/integrations/logos/frotcom-logo.png";
import verizonConnectIcon from "@/shared/img/verizon-connect-logo.png";
import wexIcon from "@/shared/img/wex-logo.png";
import arcoIcon from "@/shared/img/arco_logo.png";
import Samsara from "./components/Samsara";
import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../contexts/ToastContext";
import Snackbar from "../../../shared/components/Snackbar";
import SubscribePopup from "../../../shared/components/SubscribePopup";
import Geotab from "./components/Geotab";
import Streamline from "./components/Streamline";
import InfoPopup from "../../../shared/components/InfoPopup";
import ActionPopup from "../../../shared/components/ActionPopup";
import { useSelector } from "react-redux";
import VerizonConnect from "./components/VerizonConnect";
import { getVehicles, post, postApi } from "../../../api/getUser";
import { addSnackbarData } from "../../../features/snackbar/snackbarSlice";
import {
  providerNameMapping,
  providerTypeMapping,
} from "../../../shared/constants";
import { useDispatch } from "react-redux";
import { updateVehiclesList } from "../../../features/vehicles/vehiclesSlice";
import { addRowData } from "../../../features/row/rowSlice";
import SafetyTrack from "./components/SafetyTrack";
import EasyTrack from "./components/EasyTrack";
import Bouncie from "./components/Bouncie";
import { useLocation } from "react-router";
import Motive from "./components/Motive";
import Wialon from "./components/Wialon";
import FleetComplete from "./components/FleetComplete";
import VestigeView from "./components/VestigeView";
import FuelCardProvider from "./components/FuelCardProvider";
import Wex from "./components/Wex";
import Targa from "./components/Targa";
import SubscribeTooltipIcon from "../../../shared/components/SubscribeTooltipIcon";
import Cartrack from "./components/Cartrack";
import Frotcom from "./components/Frotcom";

const Integrations = () => {
  const { t } = useTranslation("common");
  const {
    integrationsData,
    tag,
    setIntegrationsData,
    setFetchTelematicsOdo,
    setShowFaultCodes,
    setReload,
  } = useAuth();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const [showSubscribePopup, setShowSubscribePopup] = useState(false);
  const [subscribePopupMsg, setSubscribePopupMsg] = useState(
    t("subscribe_to_advanced_msg"),
  );
  const [showErrPopup, setShowErrPopup] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [showDeleteConnectionPopup, setShowDeleteConnectionPopup] =
    useState(false);
  const [deleteBtnLoading, setDeleteBtnLoading] = useState(false);
  const [deleteProviderId, setDeleteProviderId] = useState(null);

  const { roleId } = useSelector((state) => state.user);

  //1-Connect 2-Modify 3-Coming soon 4-Connect(disabled since other provider is connected)
  const [providerStates, setProviderStates] = useState({
    samsara: 1,
    geotab: 1,
    streamline: 1,
    verizonConnect: 1,
    safetyTrack: 1,
    easyTrack: 1,
    bouncie: 1,
    motive: 1,
    wialon: 1,
    fleetComplete: 1,
    vestigeView: 1,
    targa: 1,
    cartrack: 1,
    frotcom: 1,
  });

  const [providerStep, setProviderStep] = useState({
    samsara: 0,
    geotab: 0,
    streamline: 0,
    verizonConnect: 0,
    safetyTrack: 0,
    easyTrack: 0,
    bouncie: 0,
    motive: 0,
    wialon: 0,
    fleetComplete: 0,
    vestigeView: 0,
    targa: 0,
    cartrack: 0,
    frotcom: 0,
  });

  //1 -> Connect, 2 -> Setup in progress, 3 -> Disconnect, 4 -> Connect Disabled, 5 -> Coming soon
  const [fuelCardStates, setFuelCardStates] = useState({
    wex: 1,
    arco: 5,
  });
  const [showWexPopup, setShowWexPopup] = useState(false);

  //auth code for bouncie/motive/wialon oauth
  const [authCode, setAuthCode] = useState("");

  const handleProviderStepChange = (providerName, step) =>
    setProviderStep((prev) => ({ ...prev, [providerName]: step }));

  const [telematicsConnected, setTelematicsConnected] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [showAllTelematics, setShowAllTelematics] = useState(false);

  //Open bouncie integration popup after auth redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const state = searchParams.get("state");
    const code = searchParams.get("code");
    if (state === "bouncie_auth") {
      setAuthCode(code);
      handleProviderStepChange("bouncie", 1);
    }
  }, []);

  //Update providerStates
  useEffect(() => {
    let providerStatesObj = {
      samsara: 1,
      geotab: 1,
      streamline: 1,
      safetyTrack: 1,
      verizonConnect: 1,
      easyTrack: 1,
      bouncie: 1,
      motive: 1,
      wialon: 1,
      frotcom: 1,
      fleetComplete: 1,
      vestigeView: 1,
      targa: 1,
      cartrack: 1,
    };
    if (integrationsData?.length > 0) {
      for (let intObj of integrationsData) {
        const { provider, provider_type, service_enabled, service_type } =
          intObj;

        if (provider_type === 1) {
          providerStatesObj = {
            samsara: 4,
            geotab: 4,
            streamline: 4,
            safetyTrack: 4,
            verizonConnect: 4,
            easyTrack: 4,
            bouncie: 4,
            motive: 4,
            wialon: 4,
            fleetComplete: 4,
            vestigeView: 4,
            targa: 4,
            cartrack: 4,
            frotcom: 4,
          };
          providerStatesObj[providerTypeMapping[provider]] = 2;
        }

        //For WEX
        if (provider_type === 2 && provider === 13) {
          const wexStatus = service_enabled === 1 ? 3 : 2;
          setFuelCardStates((prev) => ({ ...prev, wex: wexStatus }));
        }
      }
    }
    setProviderStates(providerStatesObj);
    const isTelematicsConnected = Object.values(providerStatesObj).includes(2);
    setTelematicsConnected(isTelematicsConnected);
  }, [integrationsData]);

  //Open Motive integration popup after auth redirect only if tag is advanced-user
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const state = searchParams.get("state");
    const code = searchParams.get("code");

    if (state === "motive_auth" && code) {
      if (telematicsConnected) {
        handleProviderStepChange("motive", 0);
        setErrMsg(t("telematics_already_connected_msg"));
        setShowErrPopup(true);
        return;
      }

      if (tag && roleId) {
        if (tag !== "advanced-user") {
          handleProviderStepChange("motive", 0);
          setSubscribePopupMsg(t("subscribe_to_advanced_msg"));
          setShowSubscribePopup(true);
          return;
        }
        setAuthCode(code);
        handleProviderStepChange("motive", 1);
      }
    }
  }, [tag, roleId, telematicsConnected, t]);

  //Open Wialon integration popup after auth redirect only if tag is advanced-user
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const state = searchParams.get("state");
    const accessToken = searchParams.get("access_token");

    if (state === "wialon_auth" && accessToken) {
      if (telematicsConnected) {
        handleProviderStepChange("wialon", 0);
        setErrMsg(t("telematics_already_connected_msg"));
        setShowErrPopup(true);
        return;
      }

      if (tag && roleId) {
        if (tag !== "advanced-user") {
          handleProviderStepChange("wialon", 0);
          setSubscribePopupMsg(t("subscribe_to_advanced_msg"));
          setShowSubscribePopup(true);
          return;
        }
        setAuthCode(accessToken);
        handleProviderStepChange("wialon", 1);
      }
    }
  }, [tag, roleId, telematicsConnected, t]);

  const handleProviderClick = async (providerName) => {
    if (tag === "free-user") {
      setSubscribePopupMsg(t("free_user_telematics_msg"));
      setShowSubscribePopup(true);
      return;
    }

    const subRes = await postApi(
      {
        querystring: "get_subscription_status",
      },
      "subscription",
    );
    if (subRes.success) {
      console.log("subRes", subRes);
      const { success, ...subscription_data } = subRes;
      const { telematics_stripe_status, product } = subscription_data;

      //Allow Telematics for 1,2,3 users
      if (
        telematics_stripe_status !== "active" &&
        !["1", "2", "3"].includes(product)
      ) {
        setSubscribePopupMsg(t("paid_user_telematics_msg"));
        setShowSubscribePopup(true);
        return;
      }
    }

    if (roleId !== 1 && providerStates[providerName] === 1) {
      setErrMsg(t("integ_connection_access_restricted_msg"));
      setShowErrPopup(true);
      return;
    }

    //Connect -> Step 1
    if (providerStates[providerName] === 1) {
      handleProviderStepChange(providerName, 1);
    }

    //Manage -> Step 7
    if (providerStates[providerName] === 2) {
      handleProviderStepChange(providerName, 7);
    }
  };

  const handleFuelCardClick = async (providerName) => {
    if (providerName === "wex") {
      if (tag === "free-user") {
        setSubscribePopupMsg(t("wex_sub_free_user_msg"));
        setShowSubscribePopup(true);
        return;
      }
      const subRes = await postApi(
        {
          querystring: "get_subscription_status",
        },
        "subscription",
      );
      if (subRes.success) {
        console.log("subRes", subRes);
        const { success, ...subscription_data } = subRes;
        const { fuel_card_stripe_status } = subscription_data;
        if (fuel_card_stripe_status !== "active") {
          setSubscribePopupMsg(t("wex_sub_paid_user_msg"));
          setShowSubscribePopup(true);
          return;
        }
      }

      setShowWexPopup(true);
    }
  };

  const fetchIntegrationsData = async () => {
    setShowFaultCodes(false);
    setFetchTelematicsOdo(false);
    const apiData = {
      querystring: "get_integrations_data",
    };
    const res = await post("integrations", apiData);
    console.log(res);
    if (res.success) {
      const { data } = res;
      if (data.length > 0) {
        for (let intObj of data) {
          const { provider, provider_type, service_enabled, service_type } =
            intObj;

          const faultCodesProviders = [1, 2, 4, 10];
          if (provider_type === 1 && faultCodesProviders.includes(provider)) {
            setShowFaultCodes(true);
          }

          if (
            provider_type === 1 &&
            service_enabled === 1 &&
            service_type === 1
          ) {
            setFetchTelematicsOdo(true);
          }

          setIntegrationsData(data);
        }
      }
    } else {
      setIntegrationsData([]);
      setShowFaultCodes(false);
      setFetchTelematicsOdo(false);
    }
  };

  const handleDeleteConnection = async (providerId) => {
    if (roleId !== 1) {
      setErrMsg(t("integ_modify_access_restricted_msg"));
      setShowErrPopup(true);
      setShowDeleteConnectionPopup(false);
      return;
    }
    setDeleteBtnLoading(true);
    try {
      const apiData = {
        querystring: "delete_integration",
        provider: providerId,
        provider_type: 1,
      };

      const res = await post("integrations", apiData);
      if (res.success) {
        await fetchIntegrationsData();
        setShowDeleteConnectionPopup(false);
        showToast("success", t("connection_deleted_success_msg"));
        handleProviderStepChange(providerTypeMapping[providerId], 0);
      } else {
        showToast(
          "error",
          t("listing_err_msg") || "Failed to delete connection",
        );
      }
    } catch (error) {
      console.error("Error deleting connection:", error);
      showToast(
        "error",
        t("listing_err_msg") ||
          "An error occurred while deleting the connection",
      );
    } finally {
      setDeleteBtnLoading(false);
    }
  };

  const handleDeleteConnectionClick = (providerId) => {
    window.history.replaceState({}, "", window.location.pathname);
    setDeleteProviderId(providerId);
    setShowDeleteConnectionPopup(true);
  };

  const handleImportAssets = async (
    provider,
    vehicles,
    setImportBtnLoading,
  ) => {
    setImportBtnLoading(true);
    const selectedVehicles = vehicles
      .filter((v) => v.isChecked)
      .map((veh) => {
        const { isChecked, ...rest } = veh;
        return { ...rest };
      });

    const subRes = await postApi(
      {
        querystring: "get_subscription_status",
      },
      "commonNew",
    );
    if (subRes.success) {
      const { subscription_data } = subRes;
      const { no_of_vehicles, vehiclecount, product } = subscription_data;

      let noOfVehicles = no_of_vehicles;
      if (product == 1) {
        noOfVehicles = 30;
      }
      if (product == 2) {
        noOfVehicles = 100;
      }
      if (product == 3) {
        noOfVehicles = 300;
      }

      const vehiclesToImport =
        selectedVehicles?.length > 0
          ? selectedVehicles?.filter((v) => v?.isNew === 1)?.length
          : 0;

      const totalVehicles = +vehiclesToImport + +vehiclecount;
      if (totalVehicles > noOfVehicles) {
        setErrMsg(
          t("veh_import_limit_reached_msg", {
            count: noOfVehicles,
            vehiclesToImport: totalVehicles,
          }),
        );
        setShowErrPopup(true);
        setImportBtnLoading(false);
        return;
      }
    }

    console.log("selectedVehicles", selectedVehicles);
    const apiData = {
      querystring: "add_telematics_vehicles",
      data: selectedVehicles,
    };
    const res = await post("integrations", apiData);
    setImportBtnLoading(false);
    if (res.success) {
      dispatch(
        addSnackbarData({
          msg: t("count_vehicles_synced_msg", {
            count: selectedVehicles.length,
            providerName: providerNameMapping[provider],
          }),
          type: 1,
        }),
      );
    } else {
      dispatch(
        addSnackbarData({
          msg: t("listing_err_msg"),
          type: 2,
        }),
      );
    }
    setShowSnackbar(true);
    setTimeout(() => {
      setShowSnackbar(false);
      dispatch(
        addSnackbarData({
          msg: "",
          type: 1,
        }),
      );
    }, 3000);
    handleProviderStepChange(providerTypeMapping[provider], 6);
    const vehiclesList = await getVehicles();
    dispatch(updateVehiclesList(vehiclesList));
  };

  // Full unfiltered provider lists (used as base for search + pinned strip)
  const telematicsProviders = useMemo(
    () => [
      {
        key: "samsara",
        name: "Samsara",
        icon: samsaraIcon,
        state: providerStates.samsara,
        type: "telematics",
      },
      {
        key: "geotab",
        name: "Geotab",
        icon: geoTabIcon,
        state: providerStates.geotab,
        type: "telematics",
      },
      {
        key: "streamline",
        name: "Streamline",
        icon: streamlineIcon,
        state: providerStates.streamline,
        type: "telematics",
      },
      {
        key: "safetyTrack",
        name: "Safety Track",
        icon: safetyTrackIcon,
        state: providerStates.safetyTrack,
        type: "telematics",
      },
      {
        key: "verizonConnect",
        name: "Verizon Connect",
        icon: verizonConnectIcon,
        state: providerStates.verizonConnect,
        type: "telematics",
      },
      {
        key: "easyTrack",
        name: "Easy Track",
        icon: easyTrackIcon,
        state: providerStates.easyTrack,
        type: "telematics",
      },
      {
        key: "bouncie",
        name: "Bouncie",
        icon: bouncieIcon,
        state: providerStates.bouncie,
        type: "telematics",
      },
      {
        key: "motive",
        name: "Motive",
        icon: motiveIcon,
        state: providerStates.motive,
        type: "telematics",
      },
      {
        key: "wialon",
        name: "Wialon",
        icon: wialonIcon,
        state: providerStates.wialon,
        type: "telematics",
      },
      {
        key: "fleetComplete",
        name: "Fleet Complete",
        icon: fleetCompleteIcon,
        state: providerStates.fleetComplete,
        type: "telematics",
      },
      {
        key: "vestigeView",
        name: "Vestige View",
        icon: vestigeViewIcon,
        state: providerStates.vestigeView,
        type: "telematics",
      },
      {
        key: "targa",
        name: "Targa",
        icon: targaIcon,
        state: providerStates.targa,
        type: "telematics",
      },
      {
        key: "cartrack",
        name: "Cartrack",
        icon: cartrackIcon,
        state: providerStates.cartrack,
        type: "telematics",
      },
      {
        key: "frotcom",
        name: "Frotcom",
        icon: frotcomIcon,
        state: providerStates.frotcom,
        type: "telematics",
      },

    ],
    [providerStates],
  );

  const fuelCardProvidersList = useMemo(
    () => [
      {
        key: "wex",
        name: "WEX",
        icon: wexIcon,
        state: fuelCardStates.wex,
        type: "fuel",
      },
      {
        key: "arco",
        name: "ARCO",
        icon: arcoIcon,
        state: fuelCardStates.arco,
        type: "fuel",
      },
    ],
    [fuelCardStates],
  );

  const filteredTelematicsProviders = useMemo(() => {
    if (!searchQuery.trim()) return telematicsProviders;
    const q = searchQuery.toLowerCase().trim();
    return telematicsProviders.filter((p) => p.name.toLowerCase().includes(q));
  }, [searchQuery, telematicsProviders]);

  const filteredFuelCardProviders = useMemo(() => {
    if (!searchQuery.trim()) return fuelCardProvidersList;
    const q = searchQuery.toLowerCase().trim();
    return fuelCardProvidersList.filter((p) =>
      p.name.toLowerCase().includes(q),
    );
  }, [searchQuery, fuelCardProvidersList]);

  const connectedProviders = useMemo(
    () => [
      ...telematicsProviders.filter((p) => p.state === 2),
      ...fuelCardProvidersList.filter((p) => p.state === 3),
    ],
    [telematicsProviders, fuelCardProvidersList],
  );

  const showAllExpanded =
    activeFilter === "telematics" || showAllTelematics || !!searchQuery.trim();
  const visibleTelematics = showAllExpanded
    ? filteredTelematicsProviders
    : filteredTelematicsProviders.slice(0, 6);
  const hasMoreTelematics =
    filteredTelematicsProviders.length > 6 &&
    !searchQuery.trim() &&
    activeFilter !== "telematics";

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setShowAllTelematics(false);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setShowAllTelematics(false);
  };

  return (
    <>
      {showSnackbar && (
        <Snackbar
          showSnackbar={showSnackbar}
          setShowSnackbar={setShowSnackbar}
        />
      )}
      {providerStep.samsara !== 0 && (
        <Samsara
          step={providerStep.samsara}
          setStep={(step) => handleProviderStepChange("samsara", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.geotab !== 0 && (
        <Geotab
          step={providerStep.geotab}
          setStep={(step) => handleProviderStepChange("geotab", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.streamline !== 0 && (
        <Streamline
          step={providerStep.streamline}
          setStep={(step) => handleProviderStepChange("streamline", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.safetyTrack !== 0 && (
        <SafetyTrack
          step={providerStep.safetyTrack}
          setStep={(step) => handleProviderStepChange("safetyTrack", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.verizonConnect !== 0 && (
        <VerizonConnect
          step={providerStep.verizonConnect}
          setStep={(step) => handleProviderStepChange("verizonConnect", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.easyTrack !== 0 && (
        <EasyTrack
          step={providerStep.easyTrack}
          setStep={(step) => handleProviderStepChange("easyTrack", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.bouncie !== 0 && (
        <Bouncie
          step={providerStep.bouncie}
          setStep={(step) => handleProviderStepChange("bouncie", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          authCode={authCode}
          setAuthCode={setAuthCode}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.motive !== 0 && (
        <Motive
          step={providerStep.motive}
          setStep={(step) => handleProviderStepChange("motive", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          authCode={authCode}
          setAuthCode={setAuthCode}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.wialon !== 0 && (
        <Wialon
          step={providerStep.wialon}
          setStep={(step) => handleProviderStepChange("wialon", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          authCode={authCode}
          setAuthCode={setAuthCode}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.fleetComplete !== 0 && (
        <FleetComplete
          step={providerStep.fleetComplete}
          setStep={(step) => handleProviderStepChange("fleetComplete", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.vestigeView !== 0 && (
        <VestigeView
          step={providerStep.vestigeView}
          setStep={(step) => handleProviderStepChange("vestigeView", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.targa !== 0 && (
        <Targa
          step={providerStep.targa}
          setStep={(step) => handleProviderStepChange("targa", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.cartrack !== 0 && (
        <Cartrack
          step={providerStep.cartrack}
          setStep={(step) => handleProviderStepChange("cartrack", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {providerStep.frotcom !== 0 && (
        <Frotcom
          step={providerStep.frotcom}
          setStep={(step) => handleProviderStepChange("frotcom", step)}
          showSnackbar={true}
          setShowSnackbar={setShowSnackbar}
          fetchIntegrationsData={fetchIntegrationsData}
          handleImportAssets={handleImportAssets}
          handleDeleteConnection={handleDeleteConnectionClick}
        />
      )}
      {showWexPopup && (
        <Wex
          status={fuelCardStates.wex}
          onClose={() => setShowWexPopup(false)}
          onFormSubmitted={async () => {
            await fetchIntegrationsData();
            setShowWexPopup(false);
          }}
        />
      )}
      {showSubscribePopup && (
        <SubscribePopup
          showPopup={showSubscribePopup}
          setShowPopup={setShowSubscribePopup}
          message={subscribePopupMsg}
        />
      )}
      {showErrPopup && (
        <InfoPopup
          showPopup={showErrPopup}
          setShowPopup={setShowErrPopup}
          message={errMsg}
          icon={{ className: "fi fi-rr-cross-circle", color: "red" }}
        />
      )}
      {showDeleteConnectionPopup && (
        <ActionPopup
          showPopup={showDeleteConnectionPopup}
          setShowPopup={setShowDeleteConnectionPopup}
          handleYes={() => handleDeleteConnection(deleteProviderId)}
          handleNo={() => setShowDeleteConnectionPopup(false)}
          message={t("delete_connection_confirm_msg")}
          yesBtnLoading={deleteBtnLoading}
        />
      )}

      <Container>
        <Row className="mx-1 d-flex align-items-center justify-content-around">
          <Col>
            <ColumnHeading>{t("integrations")}</ColumnHeading>
          </Col>
        </Row>
        <Row className="p-2">
          <Col>
            <Card>
              <CardBody formcard="true">
                {/* Search */}
                <Row className="mb-3">
                  <Col>
                    <SearchInput
                      type="search"
                      placeholder={t("search_integrations")}
                      onChange={handleSearchChange}
                      value={searchQuery}
                      style={{ width: "100%", maxWidth: "400px" }}
                    />
                  </Col>
                </Row>

                {/* Filter chips */}
                <ChipsRow>
                  {[
                    {
                      id: "all",
                      label: t("all"),
                      count:
                        filteredTelematicsProviders?.length +
                        filteredFuelCardProviders?.length,
                    },
                    {
                      id: "telematics",
                      label: t("telematics"),
                      count: filteredTelematicsProviders?.length,
                    },
                    {
                      id: "fuel",
                      label: t("fuel_cards"),
                      count: filteredFuelCardProviders?.length,
                    },
                  ].map((chip) => (
                    <Chip
                      key={chip.id}
                      active={activeFilter === chip.id}
                      onClick={() => handleFilterChange(chip.id)}
                    >
                      {chip.label}{" "}
                      <ChipCount active={activeFilter === chip.id}>
                        {chip.count}
                      </ChipCount>
                    </Chip>
                  ))}
                </ChipsRow>

                {/* Your connections pinned strip */}
                {activeFilter === "all" && connectedProviders.length > 0 && (
                  <PinnedStrip>
                    <PinnedHeader>
                      <PinnedTitle>{t("your_connections")}</PinnedTitle>
                      <PinnedSub>
                        {t("count_active", {
                          count: connectedProviders.length,
                        })}
                      </PinnedSub>
                    </PinnedHeader>
                    <IntegrationsGrid style={{ marginTop: "14px" }}>
                      {connectedProviders.map((p) =>
                        p.type === "telematics" ? (
                          <SingleProvider
                            key={`pinned-${p.key}`}
                            providerName={p.name}
                            providerState={p.state}
                            providerIcon={p.icon}
                            handleClick={() => handleProviderClick(p.key)}
                          />
                        ) : (
                          <FuelCardProvider
                            key={`pinned-${p.key}`}
                            providerName={p.name}
                            providerState={p.state}
                            providerIcon={p.icon}
                            handleClick={() => handleFuelCardClick(p.key)}
                          />
                        ),
                      )}
                    </IntegrationsGrid>
                  </PinnedStrip>
                )}

                {/* Telematics section */}
                {(activeFilter === "all" || activeFilter === "telematics") &&
                  filteredTelematicsProviders.length > 0 && (
                    <SectionBlock>
                      <SectionHead>
                        <SectionTitle>{t("telematics")}</SectionTitle>
                        {/* {tag === "essential-user" && (
                          <SubscribeTooltipIcon
                            message={t("subscribe_to_advanced_msg")}
                          />
                        )} */}
                      </SectionHead>
                      <Desc className="mb-2">
                        {t("by_integrating_telematics_desc")}
                      </Desc>
                      <IntegrationsGrid>
                        {visibleTelematics.map((p) => (
                          <SingleProvider
                            key={p.key}
                            providerName={p.name}
                            providerState={p.state}
                            providerIcon={p.icon}
                            handleClick={() => handleProviderClick(p.key)}
                          />
                        ))}
                      </IntegrationsGrid>
                      {hasMoreTelematics && (
                        <div>
                          <ShowMoreLink
                            onClick={() =>
                              setShowAllTelematics((prev) => !prev)
                            }
                          >
                            {showAllTelematics
                              ? t("show_less")
                              : t("show_all_telematics", {
                                  count: filteredTelematicsProviders.length,
                                })}
                          </ShowMoreLink>
                          <ShowMoreArrow open={showAllTelematics}>
                            &#9658;
                          </ShowMoreArrow>
                        </div>
                      )}
                    </SectionBlock>
                  )}

                {/* Fuel Cards section */}
                {(activeFilter === "all" || activeFilter === "fuel") &&
                  filteredFuelCardProviders.length > 0 && (
                    <SectionBlock>
                      <SectionHead>
                        <SectionTitle>{t("fuel_cards")}</SectionTitle>
                      </SectionHead>
                      <IntegrationsGrid>
                        {filteredFuelCardProviders.map((p) => (
                          <FuelCardProvider
                            key={p.key}
                            providerName={p.name}
                            providerState={p.state}
                            providerIcon={p.icon}
                            handleClick={() => handleFuelCardClick(p.key)}
                          />
                        ))}
                      </IntegrationsGrid>
                    </SectionBlock>
                  )}

                {/* No search results */}
                {filteredTelematicsProviders.length === 0 &&
                  filteredFuelCardProviders.length === 0 && (
                    <NoResultsMessage>
                      {t("no_integrations_found")}
                    </NoResultsMessage>
                  )}

                {/* Footer */}
                <Row className="mt-4">
                  <Col>
                    <Desc>
                      {t("integration_reach_out_to_sf_msg")}{" "}
                      <a href="mailto:support@simply-fleet.com">
                        support@simply-fleet.com
                      </a>
                    </Desc>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Integrations;

//styles
export const IntegrationsImg = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
`;

const ChipsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 15px;
  border: 1px solid ${(p) => (p.active ? "#2c4a5f" : "#d0d5dd")};
  background: ${(p) => (p.active ? "#2c4a5f" : "#fff")};
  color: ${(p) => (p.active ? "#fff" : "#475467")};
  border-radius: 999px;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.12s ease;

  &:hover {
    border-color: ${(p) => (p.active ? "#223a4b" : "#98a2b3")};
    background: ${(p) => (p.active ? "#223a4b" : "#fff")};
  }
`;

const ChipCount = styled.span`
  opacity: ${(p) => (p.active ? 0.75 : 0.6)};
  font-weight: 400;
`;

const PinnedStrip = styled.div`
  background: #fbfdfc;
  border: 1px solid #d6e9e0;
  border-radius: 14px;
  padding: 20px 22px;
  margin-bottom: 22px;
`;

const PinnedHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const PinnedTitle = styled.span`
  font-size: 15.5px;
  font-weight: 600;
  color: #0f4c3a;
`;

const PinnedSub = styled.span`
  font-size: 13px;
  color: #667085;
`;

const IntegrationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const SectionBlock = styled.div`
  margin-bottom: 28px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 15.5px;
  font-weight: 600;
`;

const ShowMoreLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  color: #2c4a5f;
  font-size: 13.5px;
  font-weight: 500;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 4px;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

const ShowMoreArrow = styled.span`
  display: inline-block;
  font-size: 11px;
  transition: transform 0.15s;
  transform: ${(p) => (p.open ? "rotate(90deg)" : "rotate(0deg)")};
`;

const NoResultsMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #667085;
  font-style: italic;
`;
